import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { GoogleLogin } from "@react-oauth/google";
import { Navbar } from "./navbar";
import { User, Mail, Lock, ShieldCheck, Rocket, Hand, AlertTriangle } from "lucide-react";
import { getDataComptes, addCompte } from "./action";
import "../styles/login.css";

export function Login() {
    const [IsLogin, setIsLogin] = useState(true);
    const [Name, setName] = useState("");
    const [Email, setEmail] = useState("");
    const [Password, setPassword] = useState("");
    const [ConfirmPS, setConfirmPS] = useState("");
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { comptes } = useSelector((state) => state);

    useEffect(() => {
        dispatch(getDataComptes());
    }, [dispatch]);

    function handleGoogleSuccess(credentialResponse) {
        let userEmail = "";
        let userName = "";
        let googleId = "";
        
        if (credentialResponse.credential) {
            try {
                const payload = JSON.parse(atob(credentialResponse.credential.split('.')[1]));
                userEmail = payload.email;
                // As requested: "Nom complet" comes from the part before @ in the email
                userName = userEmail.split('@')[0];
                googleId = payload.sub; // Unique Google ID used as "password of his email"
            } catch (e) {
                console.error("Error decoding token", e);
                setError("Erreur lors de la récupération des données Google");
                return;
            }
        }

        // Check if user already exists in our table
        const existingUser = comptes.find(u => u.email === userEmail);

        if (!IsLogin) {
            // "Sign up with Google" mode
            if (existingUser) {
                setError("Cet email est déjà utilisé. Veuillez vous connecter.");
                setIsLogin(true); // Switch to login mode
                return;
            }

            // Create a new account in our data.json
            const nextId = (comptes.length + 1).toString();
            const newAccount = {
                id: nextId,
                username: userName, // "Nom complet" extracted from email prefix
                email: userEmail,
                password: googleId, // Representing the "password of his email" as the unique Google ID
                role: 'client',
                status: 'Actif',
                isGoogle: true,
                date: new Date().toLocaleDateString('fr-FR'),
                favoris: [],
                historique: []
            };

            dispatch(addCompte(newAccount));
            
            localStorage.setItem('user', JSON.stringify({
                id: nextId,
                name: userName,
                email: userEmail,
                role: 'client',
                isGoogle: true
            }));
            
            alert("Compte Google créé avec succès !");
            navigate("/");
        } else {
            // "Login with Google" mode
            if (!existingUser) {
                setError("Aucun compte trouvé avec cet email Google. Veuillez d'abord vous inscrire.");
                setIsLogin(false); // Switch to signup mode
                return;
            }

            // Verify if it's a Google account
            if (!existingUser.isGoogle) {
                // If they have a manual account with same email, we could link it, 
                // but the user asked to see "if he already connected".
                // We'll allow them to login but maybe note it.
            }

            // Success Login
            localStorage.setItem('user', JSON.stringify({
                id: existingUser.id,
                name: existingUser.username,
                email: existingUser.email,
                role: existingUser.role.toLowerCase(),
                profilePic: existingUser.profilePic,
                isGoogle: true
            }));
            
            if (existingUser.role.toLowerCase() === 'admin') {
                navigate('/dashbordPage');
            } else {
                navigate("/");
            }
        }
    }

    function handleGoogleError() {
        setError("Échec de la connexion avec Google");
    }

    function handleSubmit(e) {
        e.preventDefault();
        setError("");

        if (IsLogin) {
            if (!Email || !Password) {
                setError("Tous les champs sont obligatoires");
                return;
            }


            // Verify if the user exists in the comptes table from data.json
            const user = comptes.find((u) => u.email === Email);

            if (!user) {
                setError("Cet email n'existe pas. Veuillez créer un compte.");
                return;
            }

            // Check if password is correct
            if (user.password !== Password) {
                setError("Mot de passe incorrect.");
                return;
            }

            // Check user status
            if (user.status === "Bloqué" || user.status === "Désactivé") {
                setError(`Votre compte est ${user.status}. Veuillez contacter l'administration.`);
                return;
            }

            // Success login
            localStorage.setItem('user', JSON.stringify({ 
                id: user.id,
                name: user.username, 
                email: user.email, 
                role: user.role.toLowerCase(),
                profilePic: user.profilePic
            }));

            if (user.role.toLowerCase() === 'admin') {
                navigate('/dashbordPage');
            } else {
                navigate("/");
            }

        } else {
            if (!Name || !Email || !Password || !ConfirmPS) {
                setError("Tous les champs sont obligatoires");
                return;
            }
            if (Password !== ConfirmPS) {
                setError("Les mots de passe ne correspondent pas");
                return;
            }

            // Check if user already exists
            const existingUser = comptes.find((u) => u.email === Email);
            if (existingUser) {
                setError("Cet email est déjà utilisé.");
                return;
            }

            // Prepare new account data
            const nextId = (comptes.length + 1).toString();
            const newAccount = {
                id: nextId,
                username: Name,
                email: Email,
                password: Password,
                role: 'client', // Default role for new signups
                status: 'Actif',
                date: new Date().toLocaleDateString('fr-FR')
            };

            // Dispatch addCompte to save to data.json via json-server
            dispatch(addCompte(newAccount));
            
            localStorage.setItem('user', JSON.stringify({ 
                id: nextId,
                name: Name, 
                email: Email, 
                role: 'client' 
            }));

            alert("Compte créé avec succès !");
            navigate("/");
        }
    }

    return (
        <div className="login-page-container">
            <Navbar />
            <div className={`login-content-wrapper ${IsLogin ? "is-login" : "is-signup"}`}>
                <div className="login-card-modern">
                    {/* Left/Switch Side (Background side with text) */}
                    <div className="login-visual-side">
                        <div className="visual-overlay">
                            <h2 className="visual-title">{IsLogin ? "Nouveau ici ?" : "Déjà membre ?"}</h2>
                            <p className="visual-subtitle">
                                {IsLogin 
                                    ? "Rejoignez Jamas Lens pour découvrir les meilleures solutions de climatisation pour votre maison." 
                                    : "Connectez-vous pour accéder à votre profil, vos commandes et notre support."}
                            </p>
                            <button className="btn-ghost-modern" onClick={() => {
                                setIsLogin(!IsLogin);
                                setError("");
                            }}>
                                {IsLogin ? "Créer un compte" : "Se connecter"}
                            </button>
                        </div>
                    </div>

                    {/* Right/Form Side */}
                    <div className="login-form-side-modern">
                        <div className="form-header-modern">
                            <h2>{IsLogin ? <>{IsLogin && "Bon retour ! "} <Hand className="responsive-icon header-icon" size={28} /></> : <><Rocket className="responsive-icon header-icon" size={28} /> Créer un compte</>}</h2>
                            <p>
                                {IsLogin 
                                    ? "Heureux de vous revoir. Veuillez vous connecter." 
                                    : "Remplissez les informations ci-dessous."}
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="premium-auth-form">
                            {!IsLogin && (
                                <div className="premium-input-group">
                                    <label>Nom complet</label>
                                    <div className="input-wrapper">
                                        <span className="input-icon"><User size={18} /></span>
                                        <input
                                            type="text"
                                            placeholder="Nom et Prénom"
                                            value={Name}
                                            onChange={(e) => setName(e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="premium-input-group">
                                <label>Email</label>
                                <div className="input-wrapper">
                                    <span className="input-icon"><Mail size={18} /></span>
                                    <input
                                        type="email"
                                        placeholder="exemple@email.com"
                                        value={Email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="premium-input-group">
                                <label>Mot de passe</label>
                                <div className="input-wrapper">
                                    <span className="input-icon"><Lock size={18} /></span>
                                    <input
                                        type="password"
                                        placeholder="••••••••"
                                        value={Password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>

                            {!IsLogin && (
                                <div className="premium-input-group slide-in">
                                    <label>Confirmer le mot de passe</label>
                                    <div className="input-wrapper">
                                        <span className="input-icon"><ShieldCheck size={18} /></span>
                                        <input
                                            type="password"
                                            placeholder="••••••••"
                                            value={ConfirmPS}
                                            onChange={(e) => setConfirmPS(e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>
                            )}

                            {error && <div className="modern-error-msg"><AlertTriangle className="responsive-icon error-icon" size={18} /> {error}</div>}

                            <button type="submit" className="btn-glow-primary">
                                {IsLogin ? "Se connecter" : "S'inscrire"}
                            </button>
                        </form>

                        {!IsLogin && (
                            <>
                                <div className="divider-modern">
                                    <span>Ou</span>
                                </div>
                                <div className="google-auth-wrapper">
                                    <GoogleLogin
                                        onSuccess={handleGoogleSuccess}
                                        onError={handleGoogleError}
                                        theme="outline"
                                        text="signup_with"
                                        shape="rectangular"
                                        width="100%"
                                    />
                                </div>
                            </>
                        )}
                        
                        {IsLogin && (
                            <>
                                <div className="divider-modern">
                                    <span>Ou</span>
                                </div>
                                <div className="google-auth-wrapper">
                                    <GoogleLogin
                                        onSuccess={handleGoogleSuccess}
                                        onError={handleGoogleError}
                                        theme="outline"
                                        text="signin_with"
                                        shape="rectangular"
                                        width="100%"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
